import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Tooltip } from '@insurancemenu/imenu-components-lib';
import { DriveFile } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Tooltip';
import { toJS } from 'mobx';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import RenewalExhibitsModal from 'components/group-info/RenewalExhibitsModal';
import { EnrollmentType } from 'types/enrollmentService';
import { getPreSignUrl } from 'api/operations/policyService';
import { createStyles } from './styles';

interface AttachmentsProps {
  employerGroupId?: string;
}

const Attachments = observer(({ employerGroupId }: AttachmentsProps): JSX.Element => {
  const classes = createStyles();
  const { groupId } = useParams<{ groupId: string }>();

  const {
    attachmentsStore: {
      zipInfoList,
      loading,
      isRenewalExhibitsModalOpen,
      getFinalZipsByPeriodIds,
      setRenewalExhibitsModalOpenStatus,
    },
    enrollmentCardStore: { inactivePeriods },
  } = useRootStore();

  useEffect(() => {
    void getFinalZipsByPeriodIds(employerGroupId || groupId);
  }, []);

  const convertedZipInfoList = zipInfoList?.map((zipInfo) => toJS(zipInfo)).slice();
  const convertedInactivePeriods = inactivePeriods?.map((period) => toJS(period)).slice();

  const showRenewalExhibitsButton =
    convertedInactivePeriods.filter((period) => {
      return (
        (period.type === EnrollmentType.INSTALLED || period.type === EnrollmentType.RENEWAL) &&
        period.installedProposal?.productLines &&
        period.installedProposal?.productLines.length > 0
      );
    }).length > 0;

  const getZipLinkForDownload = async (zipId: string): Promise<void> => {
    const zipLink = await getPreSignUrl(zipId);
    window.open(zipLink, '_blank');
  };

  const FileToDownloadBlock = (
    fileName: string,
    id: string,
    date: string,
    onDownload: (id: string) => void,
  ): JSX.Element => {
    return (
      <div key={id} className={classes.fileWrapper}>
        {date && <p className={classes.fieldValue}>{dateFormatters.normDateToShortAmericanDate(date)}</p>}
        <Tooltip label={fileName} alignment={AlignmentTypes.BOTTOM_START}>
          <Button
            onClick={(): void => {
              onDownload(id);
            }}
            className={classes.shortButtonName}
            type="text"
            color="primary"
            size="medium"
            leftIconProp={<DriveFile />}
          >
            {fileName}
          </Button>
        </Tooltip>
      </div>
    );
  };

  const finalZipBlocks = convertedZipInfoList
    .sort((a, b) => +new Date(b.effectiveDate) - +new Date(a.effectiveDate))
    .map(({ effectiveDate, zipId, zipName }): JSX.Element => {
      return FileToDownloadBlock(zipName, zipId, effectiveDate, getZipLinkForDownload);
    });

  return (
    <>
      <header className={classes.categoryWrapper}>
        <h3 className={classes.categoryName}>Attachments</h3>
      </header>
      <div className={classes.attachmentsWrapper}>
        {!loading && (
          <>
            {convertedZipInfoList.length > 0 && (
              <>
                <p className={classes.fieldName}>Final ZIPs:</p>
                {finalZipBlocks}
              </>
            )}
            {showRenewalExhibitsButton && (
              <>
                <p className={classes.fieldName}>Other files:</p>
                <Button
                  color="primary"
                  type="text"
                  onClick={(): void => {
                    setRenewalExhibitsModalOpenStatus(true);
                  }}
                  className={classes.renewalExhibitButton}
                >
                  Renewal Exhibits
                </Button>
              </>
            )}
          </>
        )}
      </div>
      <RenewalExhibitsModal
        show={isRenewalExhibitsModalOpen}
        inactivePeriods={convertedInactivePeriods}
        onClose={(): void => {
          setRenewalExhibitsModalOpenStatus(false);
        }}
      />
    </>
  );
});

export default Attachments;
